import { baseApi } from './baseApi'

import {
  NeedsWorkFlagRequest,
  NeedsWorkItemDto,
  NeedsWorkList,
  NeedsWorkListQuery,
  NeedsWorkPostCheck,
  ParkEraClaimRequest,
  ResolveNeedsWorkRequest,
  UpdateNeedsWorkRequest,
} from 'store/types'

// A parked remit leaves the ERA pages when it is parked and may come back as
// posted or archived when its item is resolved.
const ERA_TAGS = [
  'BillingTransactionClaims',
  'BillingEraTransactions',
  'BillingEra',
  'EraExceptionQueue',
] as const

export const needsWorkApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNeedsWorkList: builder.query<NeedsWorkList, NeedsWorkListQuery>({
      query: (params) => ({ url: '/lab/billing/needs-work', params }),
      providesTags: ['NeedsWork'],
    }),
    getChargeNeedsWork: builder.query<NeedsWorkItemDto[], string>({
      query: (chargeId) => ({ url: `/lab/billing/needs-work/charges/${chargeId}` }),
      providesTags: ['NeedsWork'],
    }),
    // Whether the item's parked remit can post as received, and if not, why.
    getNeedsWorkPostCheck: builder.query<NeedsWorkPostCheck, string>({
      query: (id) => ({ url: `/lab/billing/needs-work/${id}/post-check` }),
      providesTags: ['NeedsWork'],
    }),
    flagNeedsWork: builder.mutation<{ id: string }, NeedsWorkFlagRequest>({
      query: (body) => ({ url: '/lab/billing/needs-work', method: 'POST', body }),
      invalidatesTags: ['NeedsWork', 'OrderCounts'],
    }),
    parkEraClaim: builder.mutation<{ id: string }, ParkEraClaimRequest & { eraId: string; claimId: string }>({
      query: ({ eraId, claimId, ...body }) => ({
        url: `/lab/billing/needs-work/era-reports/${eraId}/claims/${claimId}/park`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { claimId }) => [
        'NeedsWork',
        'OrderCounts',
        ...ERA_TAGS,
        { type: 'EraPostingPlan' as const, id: claimId },
      ],
    }),
    updateNeedsWork: builder.mutation<void, UpdateNeedsWorkRequest & { id: string }>({
      query: ({ id, ...body }) => ({ url: `/lab/billing/needs-work/${id}`, method: 'PUT', body }),
      invalidatesTags: ['NeedsWork'],
    }),
    resolveNeedsWork: builder.mutation<void, { id: string; note?: string }>({
      query: ({ id, ...body }) => ({ url: `/lab/billing/needs-work/${id}/resolve`, method: 'POST', body }),
      invalidatesTags: ['NeedsWork', 'OrderCounts'],
    }),
    // Posting or dismissing the parked remit is what resolves the item.
    resolveNeedsWorkRemit: builder.mutation<
      { paymentId: string | null },
      ResolveNeedsWorkRequest & { id: string; claimId?: string | null }
    >({
      query: ({ id, claimId, ...body }) => ({
        url: `/lab/billing/needs-work/${id}/resolve-remit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { claimId }) => [
        'NeedsWork',
        'OrderCounts',
        'BillingPatientPayments',
        'BillingCharges',
        ...ERA_TAGS,
        ...(claimId ? [{ type: 'EraPostingPlan' as const, id: claimId }] : []),
      ],
    }),
  }),
})

export const {
  useGetNeedsWorkListQuery,
  useGetChargeNeedsWorkQuery,
  useGetNeedsWorkPostCheckQuery,
  useFlagNeedsWorkMutation,
  useParkEraClaimMutation,
  useUpdateNeedsWorkMutation,
  useResolveNeedsWorkMutation,
  useResolveNeedsWorkRemitMutation,
} = needsWorkApi
